import { useEffect, useState, useCallback } from "react";
import { X, Images, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { get, post, btnLight } from "./shared";

export const MediaPicker = ({ onPick, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => get("/media/library").then(setData).catch(() => setData({ library: [], uploads: [] })), []);
  useEffect(() => { load(); }, [load]);
  const all = data ? [...data.library, ...data.uploads] : [];

  const remove = async (m) => {
    if (!window.confirm(`Supprimer « ${m.label} » de la bibliothèque ?`)) return;
    setBusy(m.url);
    try { await post("/media/delete", { url: m.url }); toast.success("Image supprimée de la bibliothèque"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(""); }
  };
  const restore = async () => {
    try { const r = await post("/media/restore", {}); toast.success(`${r.restored} image(s) NŪRA réaffichée(s)`); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="media-picker" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto overscroll-contain p-6" data-lenis-prevent onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><p className="font-serif text-xl text-stone-900 flex items-center gap-2"><Images size={18} /> Bibliothèque d'images NŪRA</p><button data-testid="media-picker-close" onClick={onClose} className="p-2 text-stone-500 hover:text-stone-900"><X size={18} /></button></div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-xs text-stone-500">Images créées pour la marque + vos téléversements. Cliquez sur une image pour l'utiliser, sur la corbeille pour la retirer de la bibliothèque.</p>
          {data?.hidden_count > 0 && <button type="button" data-testid="media-restore" onClick={restore} className={btnLight}><RotateCcw size={12} /> Réafficher les images NŪRA supprimées</button>}
        </div>
        {!data ? <p className="text-sm text-stone-500">…</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {all.map((m) => (
              <div key={m.url} className="relative group rounded-xl overflow-hidden border border-stone-200 hover:border-[#C85A32] transition-colors">
                <button data-testid={`media-item-${m.url.split("/").pop().split(".")[0]}`} onClick={() => { onPick(m.url); onClose(); }} className="text-left w-full">
                  <div className="aspect-[4/3] bg-stone-100"><img loading="lazy" decoding="async" src={m.url} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /></div>
                  <p className="p-2 pr-8 text-[11px] text-stone-700 truncate">{m.label}</p>
                </button>
                <button
                  type="button"
                  disabled={busy === m.url}
                  onClick={() => remove(m)}
                  title="Supprimer de la bibliothèque"
                  aria-label={`Supprimer ${m.label}`}
                  data-testid={`media-delete-${m.url.split("/").pop().split(".")[0]}`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-rose-600 flex items-center justify-center shadow-sm opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200 hover:bg-white disabled:opacity-40"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ImageField = ({ value, onChange, testId = "image-field", onUpload }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="aspect-[3/2] rounded-xl overflow-hidden bg-stone-100 border border-stone-200">{value ? <img loading="lazy" decoding="async" src={value} alt="" className="w-full h-full object-cover" /> : <div className="h-full flex items-center justify-center text-xs text-stone-400">Aucune image</div>}</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" data-testid={`${testId}-library`} onClick={() => setOpen(true)} className={btnLight}><Images size={12} /> Bibliothèque</button>
        {onUpload && <label className={`${btnLight} cursor-pointer`}>Téléverser<input data-testid={`${testId}-upload`} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} /></label>}
      </div>
      {open && <MediaPicker onPick={onChange} onClose={() => setOpen(false)} />}
    </div>
  );
};
